import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const OverlayContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: 3000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const CollectionFrame = styled(motion.div)`
  width: 100%;
  max-width: 1400px;
  height: 90vh;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const CollectionHeader = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ffd93d 50%, #6bcf7f 100%);
  padding: 15px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 3px solid #ff6b6b;
  position: relative;
  
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    padding: 10px 15px;
    min-height: auto;
  }
`;


const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

const CollectionTitle = styled.h2`
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 0.2rem;
  }
`;

const ColorDisclaimer = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  font-size: 0.7rem;
  font-style: italic;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
  }
`;

const SortContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  
  @media (max-width: 768px) {
    position: static;
    transform: none;
    justify-content: center;
    width: 100%;
    margin-top: 0.5rem;
  }
`;

const SortLabel = styled.span`
  color: white;
  font-size: 0.9rem;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.4);
`;

const SortSelect = styled.select`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid white;
  border-radius: 15px;
  padding: 5px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: white;
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const CloseButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid white;
  color: white;
  padding: 8px 16px;
  border-radius: 25px;
  cursor: pointer;
  font-weight: bold;
  font-size: 1rem;
  transition: all 0.3s ease;
  position: absolute;
  top: 10px;
  right: 15px;
  
  &:hover {
    background: white;
    color: #ff6b6b;
  }
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 0.9rem;
    top: 8px;
    right: 12px;
  }
`;

const CollectionContent = styled.div`
  width: 100%;
  height: calc(100% - 70px);
  background: #f8f9fa;
  padding: 2rem;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    height: calc(100% - 80px);
    padding: 1rem;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProductCard = styled(motion.a)`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  text-decoration: none;
  color: inherit;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    text-decoration: none;
    color: inherit;
  }
`;

const ProductImage = styled.div`
  width: 100%;
  height: 200px;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 10px;
  
  img {
    width: 95%;
    height: 95%;
    object-fit: contain;
    transition: transform 0.3s ease;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const ProductInfo = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const ProductTitle = styled.h3`
  color: #333;
  margin: 0 0 0.5rem 0;
  font-size: 1.2rem;
  font-weight: bold;
`;

const ProductDescription = styled.p`
  color: #666;
  margin: 0 0 1rem 0;
  font-size: 0.9rem;
  line-height: 1.4;
  flex: 1;
`;

const ProductPrice = styled.div`
  color: #ff6b6b;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const AddToCartButton = styled(motion.div)`
  width: 100%;
  background: linear-gradient(45deg, #ff6b6b, #ffd93d);
  color: white;
  border: none;
  padding: 0.75rem;
  border-radius: 25px;
  font-weight: bold;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  text-decoration: none;
  text-align: center;
  display: block;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.3);
    color: white;
    text-decoration: none;
  }
`;

const PlaceholderProducts = [
  {
    id: 1,
    title: "Freakn' Tee Shirt",
    description: "Show off your attitude with our iconic Freakn' Tee Shirt! Perfect for those who dare to be different and love to rock some serious chicken vibes.",
    price: "$24.99",
    image: "/assets/products/classictee/unisex-classic-tee-tropical-blue-front-68a9125bbefc5.jpg",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 2,
    title: "Freakn' Koosie",
    description: "Keep your drinks cold with style! This insulated can cooler features the beloved Chikin Lady design. Perfect for parties, picnics, or just showing off your chicken love!",
    price: "$6.50",
    image: "/assets/products/cancooler/can-cooler-white-regular-12-oz-front-68a9145e13add.jpg",
    buyUrl: "https://chikinlady.printful.me/product/can-cooler",
    category: "accessories"
  },
  {
    id: 3,
    title: "Freakn' Coffee Cup",
    description: "Start your day with a smile! This ceramic mug features the Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage!",
    price: "$12.99",
    image: "/assets/products/mug/white-ceramic-mug-with-color-inside-red-11-oz-front-68a9159f41e0d.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-coffee-cup",
    category: "home-living",
    categories: ["mugs"]
  },
  {
    id: 4,
    title: "Freakn' Tumbler",
    description: "Stay hydrated in style with our premium 20oz stainless steel tumbler featuring the iconic Chikin Lady design! Perfect for keeping your drinks cold or hot all day long.",
    price: "$24.99",
    image: "/assets/products/20ozTumbler/tapered-stainless-steel-tumbler-20-oz-white-20oz-front-68a9e10873488.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-chikin-tumbler",
    category: "home-living"
  },
  {
    id: 5,
    title: "Freakn' Travel Tumbler",
    description: "Take your Chikin Lady style on the go with our premium travel tumbler! Perfect for keeping your drinks at the perfect temperature while you're out and about.",
    price: "$27.99",
    image: "/assets/products/travelTumbler/travel-mug-with-a-handle-white-40-oz-front-68a9e4c4d1991.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-chikin-travel-tumbler",
    category: "home-living"
  },
  {
    id: 6,
    title: "Freakn' Fall Bag",
    description: "Carry your essentials in style with our seasonal fall bag featuring the iconic Chikin Lady design! Perfect for shopping, work, or everyday use.",
    price: "$24.99",
    image: "/assets/products/fallBag/all-over-print-tote-bag-black-15x15-mockup-68a9e62772e3b.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-bag",
    category: "accessories"
  },
  {
    id: 7,
    title: "Freakn' Hoodie",
    description: "Stay cozy and stylish with our premium pullover hoodie featuring the iconic Chikin Lady design! Perfect for those cooler days when you want to show off your chicken love in comfort.",
    price: "$39.99",
    image: "/assets/products/freaknHoodie/cotton-heritage-m2580-i-unisex-premium-pullover-hoodie-maroon-front-68a9e73616a72.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-hoodie",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 8,
    title: "Freakn' Fall Long Sleeve Teal Bow",
    description: "Embrace the fall season with our stylish long sleeve tee featuring the iconic Chikin Lady design with a teal plaid bow! Perfect for those crisp autumn days.",
    price: "$23.99",
    image: "/assets/products/fallLongSleeve-TEAL/unisex-long-sleeve-tee-storm-front-68a9e864b133d.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-long-sleeve-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 9,
    title: "Freakn' Fall Long Sleeve Black Bow",
    description: "Embrace the fall season with our stylish long sleeve tee featuring the iconic Chikin Lady design with a black plaid bow! Perfect for those crisp autumn days.",
    price: "$23.99",
    image: "/assets/products/fallLongSleeve-BLACK/unisex-long-sleeve-tee-heather-mauve-front-68a9e9df33cf3.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-long-sleeve-tee-68a87c4629374",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 10,
    title: "Freakn' Fall Long Sleeve Red Bow",
    description: "Embrace the fall season with our stylish long sleeve tee featuring the iconic Chikin Lady design with a red plaid bow! Perfect for those crisp autumn days.",
    price: "$23.99",
    image: "/assets/products/fallLongSleeve-RED/unisex-long-sleeve-tee-military-green-front-68a9f4180975e.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-long-sleeve-tee-68a87d6b93e03",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 11,
    title: "Freakn' Sticker 3 x 3",
    description: "Show off your Chikin Lady love with our high-quality 3x3 inch vinyl sticker! Perfect for laptops, water bottles, cars, or anywhere you want to display your chicken pride.",
    price: "$3.99",
    image: "/assets/products/sticker3x3/kiss-cut-stickers-white-3x3-front-68a9f58a44066.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-sticker-3-x-3",
    category: "accessories"
  },
  {
    id: 12,
    title: "Freakn' Mouse Pad",
    description: "Upgrade your workspace with our premium mouse pad featuring the iconic Chikin Lady design! Perfect for smooth mouse movement and adding some chicken flair to your desk setup.",
    price: "$12.99",
    image: "/assets/products/mousePad/mouse-pad-white-front-68a9f629917f4.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-mouse-pad",
    category: "home-living"
  },
  {
    id: 13,
    title: "Freakn' Hat",
    description: "Top off your look with our classic dad hat featuring the iconic Chikin Lady design! Perfect for sunny days, outdoor adventures, or just showing off your chicken love in style.",
    price: "$18.00",
    image: "/assets/products/freaknHat/classic-dad-hat-green-camo-front-68a9ffc133014.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-hat",
    category: "accessories"
  },
  {
    id: 14,
    title: "Freakn' Fall Hoodie Black Bow",
    description: "Stay cozy and stylish with our premium fall hoodie featuring the iconic Chikin Lady design with a black plaid bow! Perfect for those crisp autumn days when you want to show off your chicken love in comfort.",
    price: "$39.99",
    image: "/assets/products/freaknFallhoodie-BLACK/cotton-heritage-m2580-i-unisex-premium-pullover-hoodie-vintage-black-front-68aa03dc581fb.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-unisex-hoodie-black-bow",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 15,
    title: "Freakn' Fall Hoodie Red Bow",
    description: "Stay cozy and stylish with our premium fall hoodie featuring the iconic Chikin Lady design with a red plaid bow! Perfect for those crisp autumn days when you want to show off your chicken love in comfort.",
    price: "$39.99",
    image: "/assets/products/freaknFallHoodie-RED/cotton-heritage-m2580-i-unisex-premium-pullover-hoodie-military-green-front-68aa046352ec3.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-hoodie-red-plaid",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 16,
    title: "Freakn' Fall Hoodie Teal Bow",
    description: "Stay cozy and stylish with our premium fall hoodie featuring the iconic Chikin Lady design with a teal plaid bow! Perfect for those crisp autumn days when you want to show off your chicken love in comfort.",
    price: "$39.99",
    image: "/assets/products/freaknFallHoodie-TEAL/cotton-heritage-m2580-i-unisex-premium-pullover-hoodie-maroon-front-68aa052684321.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fall-hoodie-teal-plaid",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 17,
    title: "Freakn' Spiral Notebook - PLAIN",
    description: "Stay organized and stylish with our premium spiral notebook featuring the iconic Chikin Lady design! Perfect for notes, journaling, or keeping track of your chicken-related thoughts and ideas.",
    price: "$16.00",
    image: "/assets/products/spiralNotebook-PLAIN/spiral-notebook-white-front-68aa09211b6d4.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-spiral-notebook",
    category: "home-living"
  },
  {
    id: 18,
    title: "Freakn' Tote Bag",
    description: "Carry your essentials in style with our premium tote bag featuring the iconic Chikin Lady design! Perfect for shopping, work, or everyday use.",
    price: "$24.99",
    image: "/assets/products/freaknToteBag/all-over-print-tote-bag-black-15x15-mockup-68aa1b6e837d4.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-tote-bag",
    category: "accessories"
  },
  {
    id: 19,
    title: "Freakn' Clear Tote Bag",
    description: "Show off your style with our transparent clear tote bag featuring the iconic Chikin Lady design! Perfect for shopping, beach trips, or making a statement wherever you go.",
    price: "$24.99",
    image: "/assets/products/freaknClearBag/clear-tote-bag-one-size-front-68aa1cb21bedd.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-clear-tote-bag",
    category: "accessories"
  },
  {
    id: 20,
    title: "Freakn' On Fire Backpack",
    description: "Turn heads with our blazing hot backpack featuring the iconic Chikin Lady design! Perfect for school, work, travel, or anywhere you want to show off your fiery chicken style.",
    price: "$49.99",
    image: "/assets/products/freaknFireBackpack/all-over-print-minimalist-backpack-white-left-68aa1de563497.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-on-fire-backpack",
    category: "accessories"
  },
  {
    id: 21,
    title: "Freakn' Spiral Notebook - Spotted",
    description: "Stay organized and stylish with our premium spotted spiral notebook featuring the iconic Chikin Lady design! Perfect for notes, journaling, or keeping track of your chicken-related thoughts and ideas.",
    price: "$16.50",
    image: "/assets/products/freaknSpiralNotebook-Spotted/spiral-notebook-white-front-68aa1f08daf40.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-spiral-notebook-68aa01eaccc79",
    category: "home-living"
  },
  {
    id: 22,
    title: "Freakn' Classic Pins (Colored)",
    description: "Show off your chicken love with these colorful Freakn' Classic Pins! Perfect for backpacks, jackets, or anywhere you want to display your attitude.",
    price: "$13.50",
    image: "/assets/products/FreaknClassicPins-colored/set-of-pin-buttons-white-1.25-front-68b1f76379648.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freaknn-collection-pins",
    category: "accessories"
  },
  {
    id: 23,
    title: "Freakn' Fried Koosie",
    description: "Keep your drinks cold with our sassy fried chicken koosie! Perfect for parties, picnics, or just showing off your chicken love while keeping beverages chilled.",
    price: "$8.50",
    image: "/assets/products/freaknKoosie/can-cooler-white-regular-12-oz-front-68b1fb04704c5.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-fried-koosie",
    category: "accessories"
  },
  {
    id: 24,
    title: "Freakn' On Fire Mouse Pad",
    description: "Set your desk on fire with our blazing hot mouse pad featuring the iconic Chikin Lady design! Perfect for smooth mouse movement and adding some fiery chicken flair to your workspace.",
    price: "$13.99",
    image: "/assets/products/freaknMousePad/mouse-pad-white-front-68b1fc82b8744.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-on-fire-mouse-pad",
    category: "home-living"
  },
  {
    id: 25,
    title: "Freakn' On Fire Clear Case for iPhone®",
    description: "Protect your iPhone with style using our blazing hot clear case featuring the iconic Chikin Lady design! Perfect for showing off your phone while keeping it safe with some fiery chicken flair.",
    price: "$17.50",
    image: "/assets/products/freaknPhoneCase/clear-case-for-iphone-iphone-11-pro-max-christmas-68b1fe1ea200f.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-on-fire-clear-case-for-iphone",
    category: "accessories"
  },
  {
    id: 26,
    title: "Freakn' Parking Metal Sign",
    description: "Make a statement with our premium metal parking sign featuring the iconic Chikin Lady design! Perfect for your driveway, garage, or anywhere you want to show off your chicken love with some serious attitude.",
    price: "$44.99",
    image: "/assets/products/freaknParkingSign/886344968b1fe7099f2a__825.png",
    buyUrl: "https://chikinlady.printful.me/product/freakn-parking-metal-sign",
    category: "home-living"
  },
  {
    id: 27,
    title: "Freakn' Long Sleeve Tee",
    description: "Stay stylish and comfortable with our premium long sleeve tee featuring the iconic Chikin Lady design! Perfect for those cooler days when you want to show off your chicken love in style.",
    price: "$28.99",
    image: "/assets/products/freaknLongSleeve/unisex-long-sleeve-tee-heather-deep-teal-front-68b20e69e6c5c.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-long-sleeve-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 28,
    title: "Freakn' Coaster",
    description: "Protect your surfaces in style with our premium coaster featuring the iconic Chikin Lady design! Perfect for keeping your tables safe while adding some chicken flair to your home decor.",
    price: "$8.50",
    image: "/assets/products/freaknCoaster/cork-back-coaster-front-68b4bde0c6886.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-coaster",
    category: "home-living"
  },
  {
    id: 29,
    title: "Freakn' Rise & Shine Coffee Mug",
    description: "Start your mornings with attitude! This ceramic coffee mug features the iconic Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage to help you rise and shine!",
    price: "$12.99",
    image: "/assets/products/freaknRiseandShineMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4bfbc48e9a.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-rise-shine-coffee-mug",
    category: "home-living"
  },
  {
    id: 30,
    title: "Freakn' Green Tea Body Wash",
    description: "Refresh and rejuvenate with this invigorating green tea body wash! This luxurious body wash features a refreshing green tea scent and is perfect for your daily shower routine. Get clean with attitude!",
    price: "$14.99",
    image: "/assets/products/freaknGreenTeabodyWash/refreshing-hand--body-wash-white-front-68b4c80a757e4.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-green-tea-body-wash",
    category: "bath-beauty"
  },
  {
    id: 31,
    title: "Freakn' Green Tea Body Lotion",
    description: "Moisturize and refresh with this luxurious green tea body lotion! This silky smooth lotion features a refreshing green tea scent and is perfect for keeping your skin soft and hydrated. Pamper yourself with attitude!",
    price: "$14.99",
    image: "/assets/products/freaknGreenTeaBodyLOTION/refreshing-hand--body-lotion-white-front-68b4c8a546482.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-green-tea-body-lotion",
    category: "bath-beauty"
  },
  {
    id: 32,
    title: "Freakn' on Fire Head Band",
    description: "Set your style on fire with our blazing hot head band featuring the iconic Chikin Lady design! Perfect for workouts, sports, or just adding some fiery chicken flair to your look.",
    price: "$11.99",
    image: "/assets/products/freaknOnFireHeadband/all-over-print-headband-white-left-front-68b77cd26bded.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-on-fire-head-band",
    category: "accessories"
  },
  {
    id: 33,
    title: "Chikin Lady Mug",
    description: "Start your day with the iconic Chikin Lady! This premium ceramic mug features the beloved Chikin Lady design and is perfect for your morning coffee, tea, or any hot beverage.",
    price: "$12.99",
    image: "/assets/products/chikinladyMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4c70da7bb1.jpg",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-mug",
    category: "home-living",
    categories: ["mugs"]
  },
  {
    id: 34,
    title: "Chikin Lady Classic Button Collection",
    description: "Show off your chicken love with this classic button collection featuring the iconic Chikin Lady design! Perfect for backpacks, jackets, or anywhere you want to display your attitude.",
    price: "$12.50",
    image: "/assets/products/chikinLadyClassicButtons/865010368a9027a6cc25__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-classic-button-collection",
    category: "accessories"
  },
  {
    id: 35,
    title: "Chikin Lady Magnetic Notepad",
    description: "Stay organized with style! This magnetic notepad features the iconic Chikin Lady design and sticks to any metal surface. Perfect for your fridge, office, or anywhere you need to jot down notes with some chicken flair!",
    price: "$12.50",
    image: "/assets/products/chikinLadyMagneticNotepad/866448068a9faa234561__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-magnetic-notepad",
    category: "home-living"
  },
  {
    id: 36,
    title: "Chikin Lady Backpack",
    description: "Carry your essentials in style with our premium backpack featuring the iconic Chikin Lady design! Perfect for school, work, travel, or anywhere you want to show off your chicken love while staying organized.",
    price: "$49.99",
    image: "/assets/products/chikinLadyBackpack/866528868aa0352e9b06__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/chikin-lady-backpack",
    category: "accessories"
  },
  {
    id: 37,
    title: "Cluck it. I'll write it down. Spiral Notebook",
    description: "Get organized with attitude! This spiral notebook features the sassy 'Cluck it. I'll write it down.' design. Perfect for notes, journaling, or keeping track of your thoughts with some serious chicken attitude!",
    price: "$16.50",
    image: "/assets/products/CluckItSprialNoptebook/909630168bb5e97df8cc__825.png",
    buyUrl: "https://chikinlady.printful.me/product/cluck-it-ill-write-it-down-spiral-notebook",
    category: "home-living"
  },
  {
    id: 38,
    title: "Don't Ruffle My Feathers Spiral Notebook",
    description: "Stay calm and organized! This spiral notebook features the sassy 'Don't Ruffle My Feathers' design. Perfect for notes, journaling, or keeping track of your thoughts while maintaining your cool chicken composure!",
    price: "$16.50",
    image: "/assets/products/DontRuffleSpiralNotebook/915669668bdf1ad45b19__825.png",
    buyUrl: "https://chikinlady.printful.me/product/dont-ruffle-my-feathers-spiral-notebook",
    category: "home-living"
  },
  {
    id: 39,
    title: "Guess What Unisex t-shirt",
    description: "Make a statement with this sassy unisex t-shirt featuring the 'Guess What' design! Perfect for those who love to keep people guessing with their chicken attitude and style.",
    price: "$23.99",
    image: "/assets/products/GuessWhatTeeShirt/963184268d2f1b65b7ba__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/guess-what-unisex-t-shirt",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 40,
    title: "Keep Calm Unisex classic tee",
    description: "Stay zen with this classic unisex t-shirt featuring the 'Keep Calm' design! Perfect for those who want to maintain their cool chicken composure while showing off their laid-back attitude.",
    price: "$19.99",
    image: "/assets/products/keepCalmclassicTee/963209568d2f34e685ed__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/keep-calm-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 41,
    title: "Karen Unisex Classic Tee",
    description: "Show off your sassy side with this classic t-shirt featuring the iconic Karen design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/karenClassicTee/963233368d2f4d2eab93__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 42,
    title: "Bikini Chikin Unisex classic tee",
    description: "Get ready for summer with this fun unisex classic tee featuring the sassy Bikini Chikin design! Perfect for beach days, pool parties, or just showing off your playful chicken style all year round.",
    price: "$19.99",
    image: "/assets/products/bikiniChikinTee/963461568d306fd69e6f__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/bikini-chikin-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 43,
    title: "Bandana Chikin Unisex classic tee",
    description: "Show off your wild side with this classic unisex tee featuring the sassy Bandana Chikin design! Perfect for those who love to rock the outlaw chicken look with attitude and style.",
    price: "$19.99",
    image: "/assets/products/bandanaChikinclassicTee/963534868d30b5331b37__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/bandana-chikin-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 44,
    title: "Guess Who Unisex classic tee",
    description: "Keep them guessing with this mysterious unisex classic tee featuring the 'Guess Who' design! Perfect for those who love to maintain an air of mystery while showing off their chicken attitude.",
    price: "$23.99",
    image: "/assets/products/guessWhoclassicTee/963609868d3114d76574__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/guess-who-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 45,
    title: "Guess Why Unisex classic tee",
    description: "Make them wonder with this intriguing unisex classic tee featuring the 'Guess Why' design! Perfect for those who love to keep people curious while showcasing their chicken attitude and style.",
    price: "$23.99",
    image: "/assets/products/guessWhyTee/965930468d42af1361c9__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/guess-why-unisex-classic-tee-copy",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 46,
    title: "Gamer Chik Unisex classic tee",
    description: "Level up your style with this epic unisex classic tee featuring the Gamer Chik design! Perfect for gamers who love to show off their chicken attitude while dominating the virtual world.",
    price: "$19.99",
    image: "/assets/products/gamerChikTee/967076968d478628e6df__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/gamer-chik-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 47,
    title: "Coop Fresh Floral Body Wash",
    description: "Refresh and rejuvenate with this luxurious floral body wash! This premium body wash features a delightful floral scent and is perfect for your daily shower routine. Get clean with a touch of nature's beauty!",
    price: "$14.99",
    image: "/assets/products/coopFreshFloralBodyWash/floral-hand--body-wash-white-front-68b4c93c8fa83.jpg",
    buyUrl: "https://chikinlady.printful.me/product/coop-fresh-floral-body-wash",
    category: "bath-beauty"
  },
  // Candle Products
  {
    id: 48,
    title: "Smell My Coop (White Sage & Lavender) Scented soy candle",
    description: "Fill your home with the fresh, clean scent of a well-maintained chicken coop! This premium candle features a long-lasting burn time and natural ingredients.",
    price: "$14.99",
    image: "/assets/products/CandlesScented/whiteSageandLavender/scented-soy-candle,-9oz-white-sage-and-lavender-front-68b49ea9ddf82.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-white-sage-lavender-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 49,
    title: "Smell My Coop (Sea Salt & Orchid) Scented soy candle",
    description: "Transform your space with the refreshing aroma of ocean breezes and delicate orchid blossoms. This elegant scent will make your home feel like a coastal coop retreat - where even the chickens would want to roost!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/seaSaltOrchid/seasaltOrchid.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-sea-salt-orchid-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 50,
    title: "Smell My Coop (Peppered Passionfruit) Scented soy candle",
    description: "Spice up your life with the exotic blend of tropical passionfruit and a hint of peppery warmth. This unique scent will transport you to a tropical paradise with attitude - where even the chickens strut with extra sass!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/peppermintPassionfruit/peppermintPassionfruit.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-peppered-passionfruit-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 51,
    title: "Smell My Coop (Coconut Cream & Cardamom) Scented soy candle",
    description: "Turn your coop into the ultimate zen retreat! This luxurious coconut cream and cardamom blend creates a tropical spa vibe. Even the most high-maintenance hens will approve of this comforting scent. Perfect for when your chickens need their daily relaxation session!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/coconutCreamCardamom/coconutCreamCardamom.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-coconut-cream-cardamom-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 52,
    title: "Smell My Coop (Clean Cotton) Scented soy candle",
    description: "Give your coop that fresh-out-of-the-dryer feeling! This crisp cotton scent will make even the messiest chickens feel like they're living in a luxury spa. Perfect for when your hens need to feel extra fancy and clean!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/cleanCotton/cleanCotton.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-clean-cotton-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 53,
    title: "Smell My Coop (Cinnamon Vanilla) Scented soy candle",
    description: "Warm up your coop with this cozy vanilla and cinnamon blend! Perfect for those chilly nights when your chickens need a little extra comfort. Even the grumpiest rooster will appreciate this warm hug of a scent!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/cinnamonVanilla/cinnamonVanilla.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-cinnamon-vanilla-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 54,
    title: "Smell My Coop (Christmas Warmth) Scented soy candle",
    description: "Make your coop the most festive place this holiday season! This magical Christmas scent will have your chickens clucking with holiday cheer. Perfect for when your hens want to celebrate the season in style!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/christmasWarmth/christmasWarmth.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-christmas-warmth-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 55,
    title: "Smell My Coop (Atlantis Whisper) Scented soy candle",
    description: "Dive into the mysterious depths of your coop with this enchanting ocean scent! This candle captures the essence of ancient underwater secrets. Even your chickens will feel like they're swimming in marine tranquility!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/atlantisWhisper/atlantisWhisper.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-atlantis-whisper-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 56,
    title: "Smell My Coop (Apple Harvest) Scented soy candle",
    description: "Celebrate the bounty of autumn in your coop with this crisp apple scent! This seasonal candle brings the warmth and joy of harvest time. Your chickens will feel like they're living in an apple orchard paradise!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/appleHarvest/appleHarvest.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-apple-harvest-scented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  {
    id: 57,
    title: "Smell My Coop (Barnyard Bliss) Unscented soy candle",
    description: "For chickens who prefer the pure, natural ambiance without added fragrances! This unscented candle provides the same warm glow and cozy atmosphere. Perfect for when your hens want to keep it simple and natural!",
    price: "$14.99",
    image: "/assets/products/CandlesScented/unscented/unscented2.jpg",
    buyUrl: "https://chikinlady.printful.me/product/smell-my-coop-unscented-soy-candle",
    category: "candles",
    categories: ["home-living"]
  },
  // Mug Products
  {
    id: 58,
    title: "Freakn' Rise & Shine Coffee Mug",
    description: "Start your mornings with attitude! This ceramic coffee mug features the iconic Chikin Lady design and is microwave safe. Perfect for coffee, tea, or any hot beverage to help you rise and shine!",
    price: "$12.99",
    image: "/assets/products/freaknRiseandShineMug/white-ceramic-mug-with-color-inside-red-11-oz-right-68b4bfbc48e9a.jpg",
    buyUrl: "https://chikinlady.printful.me/product/freakn-rise-shine-coffee-mug",
    category: "mugs",
    categories: ["home-living"]
  },
  {
    id: 59,
    title: "Where's My Cluckn' COFFEE! Mug",
    description: "Find your morning motivation with this sassy coffee mug featuring the iconic Chikin Lady design! Perfect for coffee, tea, or any hot beverage to help you find your cluckn' coffee!",
    price: "$12.99",
    image: "/assets/products/wheresmyCluckinCoffeeMug/893291868b4b00441334__825.png",
    buyUrl: "https://chikinlady.printful.me/product/wheres-my-cluckn-coffee-mug",
    category: "mugs",
    categories: ["home-living"]
  },
  {
    id: 60,
    title: "Karen Chikin 'Cluck Around & Find Out!' Mug",
    description: "Don't mess with this sassy Karen Chikin! This ceramic mug features the iconic 'Cluck Around & Find Out!' design and is microwave safe. Perfect for coffee, tea, or any hot beverage to show off your attitude!",
    price: "$14.99",
    image: "/assets/products/karenMug/white-glossy-mug-white-15-oz-halloween-68b4c5ae989b9.jpg",
    buyUrl: "https://chikinlady.printful.me/product/karen-chikin-cluck-around-find-out-mug",
    category: "mugs",
    categories: ["home-living"]
  },
  {
    id: 61,
    title: "Stop Cluckn' Around Mug",
    description: "Time to get serious with this sassy coffee mug! This ceramic mug features the iconic 'Stop Cluckn' Around' design and is microwave safe. Perfect for coffee, tea, or any hot beverage to motivate you to stop procrastinating!",
    price: "$12.99",
    image: "/assets/products/stopCluckinaroundMug/893424868b4bcd0c29ad__825.png",
    buyUrl: "https://chikinlady.printful.me/product/stop-cluckn-around-mug",
    category: "mugs",
    categories: ["home-living"]
  },
  {
    id: 62,
    title: "Tailor Swiftfeather & Travis Cluckce Unisex classic tee",
    description: "Celebrate the ultimate chicken power couple! This classic tee features the iconic Tailor Swiftfeather & Travis Cluckce design. Perfect for fans who love both country music and chicken attitude!",
    price: "$19.99",
    image: "/assets/products/tailorTravisTee/971395768d5d9184429b__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/tailor-swiftfeather-travis-cluckce-unisex-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 66,
    title: "Patchick Mahens Classic Tee",
    description: "Show off your attitude with this classic tee featuring the iconic Patchick Mahens design! Perfect for those who dare to be different and love to rock some serious chicken vibes.",
    price: "$23.99",
    image: "/assets/products/patMahensTshirt/990870868dec48b0ce26__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/patchick-mahens-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 67,
    title: "Sassy Nana Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Nana design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyNanaTshirt/991541768def0ca45c3c__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-granny-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 68,
    title: "Sassy Mimi Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Mimi design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyMimiTshirt/991585568def2231dcec__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-mimi-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 69,
    title: "Sassy Grandma Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Grandma design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyGrandmaTshirt/991615668def34bc1695__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-grandma-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 70,
    title: "Sassy Mawmaw Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Mawmaw design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyMawMawTshirt/991627468def3e70208a__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-mawmaw",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 71,
    title: "Sassy Granny Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Granny design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyGrannyTshirt/991635768def46fb436a__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-granny",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 72,
    title: "Sassy Meemaw Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Meemaw design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyMeemawTshirt/991644568def50d27331__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-meemaw-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 73,
    title: "Sassy Abuela Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Abuela design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyAbuelaTshirt/991682668def63a7e978__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-abuela-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 74,
    title: "Sassy Gigi Classic Tee",
    description: "Show off your sassy side with this classic tee featuring the iconic Sassy Gigi design! Perfect for those who aren't afraid to speak their mind and show their chicken attitude with style.",
    price: "$19.99",
    image: "/assets/products/sassyGigiTshirt/991684868def6b169e5f__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/sassy-gigi-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 75,
    title: "Everything is Fine Mama Chikin Classic Tee",
    description: "Keep calm and carry on with this classic tee featuring the iconic 'Everything is Fine' Mama Chikin design! Perfect for those who know how to maintain their cool chicken composure even when things get a little chaotic.",
    price: "$19.99",
    image: "/assets/products/everythingisFineTshirt/1002475468e40751a409e__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/everything-is-fine-mama-chikin-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 76,
    title: "Everything is Fine Chikin Lady Hoodie",
    description: "Stay cozy and calm with this premium hoodie featuring the iconic 'Everything is Fine' Chikin Lady design! Perfect for those cooler days when you want to maintain your cool chicken composure while staying warm and comfortable.",
    price: "$39.99",
    image: "/assets/products/everythingisFineHOODIE/1002856068e41f0c7f423__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/everything-is-fine-chikin-lady-hoodie",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 77,
    title: "Everything is Fine Chikin Lady Long Sleeve Shirt",
    description: "Stay stylish and calm with this premium long sleeve shirt featuring the iconic 'Everything is Fine' Chikin Lady design! Perfect for those cooler days when you want to maintain your cool chicken composure while staying comfortable and fashionable.",
    price: "$23.99",
    image: "/assets/products/everythingisfineLONGSLEEVETee/1002903468e4223e54b52__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/everything-is-fine-chikin-lady-long-sleeve-shirt",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 78,
    title: "Punk Rock Chikin Lady Classic Tee",
    description: "Rock out with attitude in this classic tee featuring the iconic Punk Rock Chikin Lady design! Perfect for those who love to show off their rebellious chicken spirit and aren't afraid to make a statement with their style.",
    price: "$19.99",
    image: "/assets/products/punkRockClassicTee/1017393168ead1c1575ea__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/punk-rock-chikin-lady-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 79,
    title: "Ghost Chikin Hoodie",
    description: "Haunt the night in style with this spooky hoodie featuring the iconic Ghost Chikin design! Perfect for those who love the supernatural and want to show off their mysterious chicken spirit while staying cozy and comfortable.",
    price: "$39.99",
    image: "/assets/products/ghostChikinHoodie/1008291268e667d98d4e5__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/ghost-chikin-hoodie",
    category: "apparel",
    categories: ["hoodies"]
  },
  {
    id: 80,
    title: "Ghost Chikin Classic Tee",
    description: "Haunt the day in style with this spooky classic tee featuring the iconic Ghost Chikin design! Perfect for those who love the supernatural and want to show off their mysterious chicken spirit with a touch of spectral style.",
    price: "$19.99",
    image: "/assets/products/ghostChikinClassicTee/1008299868e6685e6d40a__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/ghost-chikin-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 81,
    title: "Save A Chikin Classic Tee",
    description: "Show your support for our feathered friends with this classic tee featuring the iconic Save A Chikin design! Perfect for those who care about animal welfare and want to spread awareness while looking stylish.",
    price: "$19.99",
    image: "/assets/products/SaveAchickenTeeShirt/1019469868ec0e0c8bc37__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/save-a-chikin-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 82,
    title: "Beachin' Classic Tee",
    description: "Hit the beach in style with this classic tee featuring the iconic Beachin' Chikin design! Perfect for those sunny days when you want to show off your chicken attitude while soaking up the sun.",
    price: "$19.99",
    image: "/assets/products/BeachinClassicTee/1040618568f6a768dbe17__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/beachin-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 83,
    title: "All Lit Up Long Sleeve Shirt",
    description: "Light up the night with this electrifying long sleeve shirt featuring the iconic All Lit Up design! Perfect for those cooler days when you want to show off your bright chicken attitude while staying warm and stylish.",
    price: "$19.99",
    image: "/assets/products/AllLitUpLongSleeve/1041795968f6e7e58e4ee__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/all-lit-up-long-sleeve-shirt",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 84,
    title: "All Lit Up Classic Tee",
    description: "Light up the day with this electrifying classic tee featuring the iconic All Lit Up design! Perfect for showing off your bright chicken attitude with style and attitude.",
    price: "$19.99",
    image: "/assets/products/AllLitUpClassicTee/1047079068f929c95d2a1__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/all-lit-up-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  },
  {
    id: 85,
    title: "Thriftin' Chikin Classic Tee",
    description: "Show off your thrifty style with this classic tee featuring the iconic Thriftin' Chikin design! Perfect for those who love a good deal and want to rock some serious chicken vibes with a vintage twist.",
    price: "$19.99",
    image: "/assets/products/ThriftinChikinClassicTee/1047343368f93bcb8e684__825.webp",
    buyUrl: "https://chikinlady.printful.me/product/thriftin-chikin-classic-tee",
    category: "apparel",
    categories: ["t-shirts"]
  }
];

const ProductCollectionOverlay = ({ isOpen, onClose }) => {
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  const filteredProducts = selectedCategory === 'all' 
    ? PlaceholderProducts 
    : PlaceholderProducts.filter(product => 
        product.category === selectedCategory || 
        (selectedCategory === 't-shirts' && product.categories && product.categories.includes('t-shirts')) ||
        (selectedCategory === 'hoodies' && product.categories && product.categories.includes('hoodies')) ||
        (selectedCategory === 'home-living' && product.categories && product.categories.includes('home-living')) ||
        (selectedCategory === 'mugs' && product.categories && product.categories.includes('mugs'))
      );

  return (
    <AnimatePresence>
      {isOpen && (
        <OverlayContainer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <CollectionFrame
            initial={{ scale: 0.8, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.8, y: 50 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
          >
            <CollectionHeader>
              <HeaderContent>
                <CollectionTitle>🛒 Freakn' Collection</CollectionTitle>
                <ColorDisclaimer>*Some products have different color options available</ColorDisclaimer>
                <SortContainer>
                  <SortLabel>Sort by:</SortLabel>
                  <SortSelect 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    <option value="all">All Products</option>
                    <option value="apparel">Apparel</option>
                    <option value="t-shirts">T-shirts</option>
                    <option value="hoodies">Hoodies</option>
                    <option value="accessories">Accessories</option>
                    <option value="home-living">Home & Living</option>
                    <option value="bath-beauty">Bath & Beauty</option>
                    <option value="candles">Candles</option>
                    <option value="mugs">Mugs</option>
                  </SortSelect>
                </SortContainer>
              </HeaderContent>
              <CloseButton
                onClick={onClose}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                ✕ Close
              </CloseButton>
            </CollectionHeader>
            <CollectionContent>
              <ProductsGrid>
                {filteredProducts.map((product, index) => (
                                     <ProductCard
                     key={product.id}
                     href={product.buyUrl}
                     target="_blank"
                     rel="noopener noreferrer"
                     initial={{ opacity: 0, y: 20 }}
                     animate={{ opacity: 1, y: 0 }}
                     transition={{ duration: 0.5, delay: index * 0.1 }}
                     whileHover={{ scale: 1.02 }}
                     whileTap={{ scale: 0.98 }}
                   >
                                           <ProductImage>
                        <img 
                          src={product.image} 
                          alt={product.title}
                          onError={(e) => {
                            console.error(`Failed to load image: ${product.image}`);
                            e.target.style.display = 'none';
                          }}
                        />
                      </ProductImage>
                     <ProductInfo>
                       <ProductTitle>{product.title}</ProductTitle>
                       <ProductDescription>{product.description}</ProductDescription>
                       <ProductPrice>{product.price}</ProductPrice>
                                               <AddToCartButton
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          🛒 Buy Now
                        </AddToCartButton>
                     </ProductInfo>
                   </ProductCard>
                ))}
              </ProductsGrid>
            </CollectionContent>
          </CollectionFrame>
        </OverlayContainer>
      )}
    </AnimatePresence>
  );
};

export default ProductCollectionOverlay;
